// IANA zone for each team timezone code. Used for every conversion below —
// Intl/IANA zones apply the correct daylight-saving rules for whatever date
// is in question, which a fixed hours-offset (the old approach) can't: CST
// (America/Chicago) and AEST (Australia/Sydney) both observe DST on
// opposite schedules, while PHT (Asia/Manila) never does.
export const IANA_ZONES = {
  CST: 'America/Chicago',
  AEST: 'Australia/Sydney',
  PHT: 'Asia/Manila',
};

// Live current time/date in a given team timezone, for the sidebar clock.
export function formatLiveTime(timezoneCode) {
  const zone = IANA_ZONES[timezoneCode];
  const now = new Date();
  const time = now.toLocaleTimeString('en-US', { timeZone: zone, hour: 'numeric', minute: '2-digit' });
  const date = now.toLocaleDateString('en-US', { timeZone: zone, weekday: 'short', month: 'short', day: 'numeric' });
  return { time, date };
}

// The UTC offset (minutes, east-positive) in effect for `ianaZone` at the
// specific instant `utcMs`. Looking the offset up per-instant (rather than
// assuming one fixed value) is what makes every conversion below correct
// across a DST transition.
function offsetMinutesAt(ianaZone, utcMs) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
    .formatToParts(new Date(utcMs))
    .reduce((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
  const asUtcMs = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  return (asUtcMs - utcMs) / 60000;
}

// The wall-clock instant `logtimeUtc` (a UTC ISO timestamp) reads as in
// `ianaZone`, represented as a Date whose UTC getters give the local digits.
function toZonedWallClock(logtimeUtc, ianaZone) {
  const utcMs = new Date(logtimeUtc).getTime();
  const offsetMin = offsetMinutesAt(ianaZone, utcMs);
  return new Date(utcMs + offsetMin * 60000);
}

// The UTC instant that a wall-clock date+time in `ianaZone` represents —
// the inverse of toZonedWallClock.
function wallTimeToUtc(dateStr, timeStr, ianaZone) {
  const guessUtcMs = new Date(`${dateStr}T${timeStr}Z`).getTime();
  const offsetMin = offsetMinutesAt(ianaZone, guessUtcMs);
  return new Date(guessUtcMs - offsetMin * 60000);
}

// Converts a UTC ISO timestamp (Timelogs.logtime_utc) into the given
// timezone, DST-aware, per Handoff §4/§5.
export function toLocalTime(logtimeUtc, timezoneCode) {
  const local = toZonedWallClock(logtimeUtc, IANA_ZONES[timezoneCode]);
  const timeStr = local.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
  const dateStr = local.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${dateStr}, ${timeStr} ${timezoneCode}`;
}

export function toRelativeOrTime(logtimeUtc, timezoneCode) {
  const now = new Date();
  const then = new Date(logtimeUtc);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.round(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHours = Math.round(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  return toLocalTime(logtimeUtc, timezoneCode);
}

// "YYYY-MM-DD" for "today" in the given timezone, DST-aware — used as the
// default value for the admin dashboard's date filter.
export function todayDateStringInTz(timezoneCode) {
  return new Date().toLocaleDateString('en-CA', { timeZone: IANA_ZONES[timezoneCode] });
}

// "YYYY-MM-DD" shifted by `days` calendar days. Pure date-string
// arithmetic — a calendar day is a calendar day regardless of timezone, so
// no zone conversion belongs here.
export function shiftDateString(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// UTC instant range [start, end] covering a full calendar day ("dateStr",
// wall-clock in the given timezone) — for filtering historical logs to one
// specific day. DST-safe: start and end are each resolved independently, so
// a 23- or 25-hour day at a spring-forward/fall-back transition is still
// bounded correctly.
export function getDayRangeUtc(dateStr, timezoneCode) {
  const zone = IANA_ZONES[timezoneCode];
  const start = wallTimeToUtc(dateStr, '00:00:00.000', zone);
  const nextDayStart = wallTimeToUtc(shiftDateString(dateStr, 1), '00:00:00.000', zone);
  return { start, end: new Date(nextDayStart.getTime() - 1) };
}

// "YYYY-MM-DD" of the Monday of the week containing `dateStr` (defaults to
// today) in the given timezone, DST-aware.
export function getMondayOfWeek(dateStr, timezoneCode) {
  const zone = IANA_ZONES[timezoneCode];
  const refUtc = dateStr ? new Date(`${dateStr}T12:00:00.000Z`) : new Date();
  const localDateStr = refUtc.toLocaleDateString('en-CA', { timeZone: zone });

  const dayOfWeek = new Date(`${localDateStr}T00:00:00.000Z`).getUTCDay();
  const daysSinceMonday = (dayOfWeek + 6) % 7;
  return shiftDateString(localDateStr, -daysSinceMonday);
}

// "Mon, Jul 21 – Fri, Jul 25" label for the week starting at `mondayDateStr`.
export function getWeekRangeLabelFor(mondayDateStr) {
  const startOfWeek = new Date(`${mondayDateStr}T00:00:00.000Z`);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setUTCDate(startOfWeek.getUTCDate() + 4);
  const fmt = (d) =>
    d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${fmt(startOfWeek)} – ${fmt(endOfWeek)}`;
}

// The team's single source of truth for "what day is this" — day/week
// grouping, session pairing, "today", and the admin date filter are always
// anchored to CST, regardless of the CST/local display toggle. The toggle
// only ever changes how individual clock-times are labeled, never which
// day/week/session bucket something belongs to.
export const DAY_ANCHOR_TZ = 'CST';

// "YYYY-MM-DD" of the CST calendar day a timestamp falls on, DST-aware.
export function getCstDateKey(logtimeUtc) {
  return new Date(logtimeUtc).toLocaleDateString('en-CA', { timeZone: IANA_ZONES[DAY_ANCHOR_TZ] });
}

// Pairs a chronologically-sorted list of one person's logs into sessions
// (an IN with its matching OUT). Each session is tagged with the CST day of
// its IN — that's the day the whole session belongs to, even if the OUT
// timestamp technically falls on a later CST day (e.g. checking out the
// next morning after forgetting the night before). A session with no OUT
// yet (still checked in) or an orphaned OUT with no preceding IN in this
// log set are both handled — the latter falls back to the OUT's own day.
export function pairIntoSessions(sortedLogs) {
  const sessions = [];
  let pendingIn = null;

  for (const log of sortedLogs) {
    if (log.isIn) {
      if (pendingIn) sessions.push({ in: pendingIn, out: null });
      pendingIn = log;
    } else if (pendingIn) {
      sessions.push({ in: pendingIn, out: log });
      pendingIn = null;
    } else {
      sessions.push({ in: null, out: log });
    }
  }
  if (pendingIn) sessions.push({ in: pendingIn, out: null });

  return sessions.map((s) => ({
    ...s,
    dayKey: getCstDateKey((s.in || s.out).logtime_utc),
  }));
}
